import { CircularProgress } from './CircularProgress'
import { DeltaIndicator } from './DeltaIndicator'
import { DrilldownTrigger } from '../drilldown'
import { TabProps } from './types'
import { useFormatters } from '../../contexts/PreferencesContext'
import { useAuth } from '../../contexts/AuthContext'
import { usePortalLabels } from '../../lib/portalLabels'
import { useQualityMeasureLabel } from '../../lib/qualityLabels'

interface KPIMetricsSectionProps {
  data: TabProps['data'];
  currency: TabProps['currency'];
  drilldownFilters: Record<string, string>;
  onOTIFClick: () => void;
  onOTDClick: () => void;
}

export function KPIMetricsSection({ data, currency, drilldownFilters, onOTIFClick, onOTDClick }: KPIMetricsSectionProps) {
  // OOV / Arrears are current-state KPIs (open orders right now). The dashboard
  // date range applies to time-series KPIs (PPM/OTIF/OTD/Spend) but is meaningless
  // for "what's open today". Strip date_from/date_to from the drilldown filters
  // for these cards so the drilldown shows ALL open orders, regardless of which
  // period the user has selected on the dashboard.
  const drilldownFiltersNoDate = Object.fromEntries(
    Object.entries(drilldownFilters).filter(
      ([k]) => k !== 'date_from' && k !== 'date_to',
    ),
  );
  const { formatCurrency } = useFormatters()
  const { user } = useAuth()
  const portalLabels = usePortalLabels(user?.platform)
  const qualityLabel = useQualityMeasureLabel()

  const deliveryMode = data.delivery_metric_mode ?? 'both'
  // CR-4641 round 2: chart visibility is the AND of two signals — the
  // company-wide delivery_metric_mode admin override AND whether the metric
  // has any data after the CustomerProfile flag filter (backend sends null
  // when no rows remain). Both must be true for the card to render.
  const showOTIF = (deliveryMode === 'both' || deliveryMode === 'otif_only') && data.quality.otif !== null
  const showOTD = (deliveryMode === 'both' || deliveryMode === 'otd_only') && data.delivery.otd !== null
  const showPPM = data.quality.ppm !== null

  // CR-4670: Unified cell layout so sub-labels and values line up horizontally
  // across the three cards regardless of content type (chip / donut / text /
  // currency). Each cell has three fixed-height rows: label, value, footnote.
  const labelClass = 'text-xs text-muted-foreground font-medium uppercase tracking-wide leading-tight h-8 flex items-center justify-center text-center px-1'
  const valueClass = 'h-24 flex items-center justify-center w-full'
  const footClass = 'h-6 flex items-center justify-center'

  return (
    <div className="bg-card rounded-lg shadow-sm p-4" data-tour="dashboard-kpis">
      <div className="grid grid-cols-3 gap-4 items-stretch">
        {/* Quality Section — CR-4542: PPM + NCRs only */}
        <div className="border border-red-200 bg-red-50/30 rounded-lg p-4 flex flex-col">
          <h3 className="text-center font-semibold text-red-600 mb-4 text-sm uppercase tracking-wide">Quality</h3>
          {/* CR-4641 round 2: PPM card hides when include_in_ncr_ppm filter
              empties the queryset (data.quality.ppm === null). NCR card always
              renders; layout collapses to single column when PPM is hidden. */}
          <div className={`grid gap-3 items-start flex-1 ${showPPM ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {showPPM && (
              <DrilldownTrigger metric="ppm" filters={drilldownFilters}>
                <div className="flex flex-col items-center">
                  <p className={labelClass}>{qualityLabel}</p>
                  <div className={valueClass}>
                    <div className="bg-destructive text-destructive-foreground px-3 py-2 rounded-md">
                      <p className="text-2xl font-bold leading-none">{Math.round(data.quality.ppm ?? 0).toLocaleString()}</p>
                    </div>
                  </div>
                  <div className={footClass}>
                    {data.deltas?.ppm && (
                      <DeltaIndicator delta={data.deltas.ppm} inverse={true} format="number" />
                    )}
                  </div>
                </div>
              </DrilldownTrigger>
            )}

            <DrilldownTrigger metric="ncr" filters={drilldownFilters}>
              <div className="flex flex-col items-center">
                <p className={labelClass}>NCRs</p>
                <div className={valueClass}>
                  <div className="text-3xl font-bold leading-none">
                    {data.quality.ncrs}
                  </div>
                </div>
                <div className={footClass}>
                  {data.deltas?.ncrs && (
                    <DeltaIndicator delta={data.deltas.ncrs} inverse={true} format="number" />
                  )}
                </div>
              </div>
            </DrilldownTrigger>
          </div>
        </div>

        {/* Delivery Section — CR-4542: OTIF + OTD + Appeals */}
        <div className="border border-green-200 bg-green-50/30 rounded-lg p-4 flex flex-col">
          <h3 className="text-center font-semibold text-green-600 mb-4 text-sm uppercase tracking-wide">Delivery</h3>
          <div className={`grid gap-3 items-start flex-1 ${showOTIF && showOTD ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {showOTIF && (
              <div
                className="flex flex-col items-center cursor-pointer hover:ring-2 hover:ring-primary/20 rounded-lg transition-all"
                onClick={onOTIFClick}
                role="button"
                tabIndex={0}
                aria-label="Drill down into OTIF data"
              >
                <p className={labelClass}>OTIF</p>
                <div className={valueClass}>
                  <CircularProgress
                    percentage={data.quality.otif ?? 0}
                    color={(data.quality.otif ?? 0) >= data.quality.otifTarget ? '#ef9a9a' : '#ff5252'}
                    size={80}
                    strokeWidth={8}
                  />
                </div>
                <div className={footClass}>
                  {data.deltas?.otif && (
                    <DeltaIndicator delta={data.deltas.otif} inverse={false} format="percentage" />
                  )}
                </div>
              </div>
            )}

            {showOTD && (
              <div
                className="flex flex-col items-center cursor-pointer hover:ring-2 hover:ring-primary/20 rounded-lg transition-all"
                onClick={onOTDClick}
                role="button"
                tabIndex={0}
                aria-label="Drill down into OTD data"
              >
                <p className={labelClass}>OTD</p>
                <div className={valueClass}>
                  <CircularProgress
                    percentage={data.delivery.otd ?? 0}
                    color="#10b981"
                    size={80}
                    strokeWidth={8}
                  />
                </div>
                <div className={footClass}>
                  {data.deltas?.otd && (
                    <DeltaIndicator delta={data.deltas.otd} inverse={false} format="percentage" />
                  )}
                </div>
              </div>
            )}

            <DrilldownTrigger metric="appeals" filters={drilldownFilters}>
              <div className="flex flex-col items-center">
                <p className={labelClass}>Appeals</p>
                <div className={valueClass}>
                  <div className="text-3xl font-bold leading-none">
                    {data.delivery.appeals}
                  </div>
                </div>
                <div className={footClass} />
              </div>
            </DrilldownTrigger>
          </div>
        </div>

        {/* Cost Section — CR-4542: Open Order Value + Total Spend + Historical Arrears */}
        <div className="border border-blue-200 bg-blue-50/30 rounded-lg p-4 flex flex-col">
          <h3 className="text-center font-semibold text-blue-600 mb-4 text-sm uppercase tracking-wide">Cost</h3>
          <div className="grid grid-cols-3 gap-3 items-start flex-1">
            <DrilldownTrigger metric="open_order_value" filters={drilldownFiltersNoDate}>
              <div className="flex flex-col items-center">
                <p className={labelClass}>{portalLabels.openOrderValue}</p>
                <div className={valueClass}>
                  <div className="text-xl font-bold leading-none text-center">{formatCurrency(data.delivery.openOrderValue)}</div>
                </div>
                <div className={footClass}>
                  {data.deltas?.open_order_value ? (
                    <DeltaIndicator delta={data.deltas.open_order_value} inverse={true} format="currency" currency={currency.currentCurrency} />
                  ) : (
                    <p className="text-[10px] text-muted-foreground">All orders</p>
                  )}
                </div>
              </div>
            </DrilldownTrigger>

            <DrilldownTrigger metric="spend" filters={drilldownFilters}>
              <div className="flex flex-col items-center">
                <p className={labelClass}>{portalLabels.totalSpend}</p>
                <div className={valueClass}>
                  <div className="text-xl font-bold leading-none text-center">{formatCurrency(data.cost.totalSpend)}</div>
                </div>
                <div className={footClass}>
                  {data.deltas?.total_spend && (
                    <DeltaIndicator delta={data.deltas.total_spend} inverse={false} format="currency" currency={currency.currentCurrency} />
                  )}
                </div>
              </div>
            </DrilldownTrigger>

            <DrilldownTrigger metric="arrears" filters={drilldownFiltersNoDate}>
              <div className="flex flex-col items-center">
                <p className={labelClass}>{portalLabels.historicalArrearsValue}</p>
                <div className={valueClass}>
                  <div className="text-xl font-bold leading-none text-center">{formatCurrency(data.cost.historicalArrears)}</div>
                </div>
                <div className={footClass}>
                  {data.deltas?.arrears_value ? (
                    <DeltaIndicator delta={data.deltas.arrears_value} inverse={true} format="currency" currency={currency.currentCurrency} />
                  ) : (
                    <p className="text-[10px] text-muted-foreground">All orders</p>
                  )}
                </div>
              </div>
            </DrilldownTrigger>
          </div>
        </div>
      </div>
    </div>
  )
}
